import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { BODY } from "@/components/marketing/marketingType";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import { LandingImage, LandingSection } from "../LandingPrimitives";
import { BandHeading, type Cards } from "./cardsShared";
import { Lines } from "@/components/marketing/Lines";

/** `why-web-visual_box`: a 90% box the picture is cropped into, on a raised white card. */
function WhyWebCard({ card }: { card: Card }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        p: "16px",
        borderRadius: "16px",
        backgroundColor: marketingPalette.featureCard,
        gridColumn: "span 4",
      }}
    >
      <Box
        sx={{
          position: "relative",
          pt: "90%",
          borderRadius: "12px",
          backgroundColor: "common.white",
          boxShadow: "rgba(93, 97, 138, 0.22) 0px 4px 13px -6px",
          overflow: "hidden",
        }}
      >
        {card.icon ? (
          <LandingImage
            src={card.icon}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 1,
              height: 1,
              objectFit: "cover",
            }}
          />
        ) : null}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4.8px" }}>
        {/* `body_new.why-web`: the body ladder, at the page's own ink. */}
        <Typography component="h6" sx={BODY}>
          <Lines text={card.title} />
        </Typography>
        {card.body ? (
          <Typography
            sx={{
              color: marketingPalette.inkMuted,
              fontSize: 14.08,
              lineHeight: "21.12px",
              opacity: 0.7,
            }}
          >
            {card.body}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/**
 * `why-web_item.is-left_align-no-padding`: a left-set heading over a row of
 * picture cards, drawn on the twelve-column grid the published band lays out
 * on so each card can take four of them.
 */
function WhyWebBand({ block }: { block: Cards }) {
  return (
    // The published band carries no rhythm of its own: the sections it sits
    // between open and close the gap around it.
    <LandingSection padding="none">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "32px",
          [TINY]: { gap: "24px" },
        }}
      >
        <BandHeading block={block} gap="8px" align="left" rail="404.8px" />
        <Box
          sx={{
            display: "grid",
            width: 1,
            gap: "16px",
            gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
            [NOT_WIDE]: { gridTemplateColumns: "repeat(8, minmax(0, 1fr))" },
            [MID]: { display: "flex", flexDirection: "column" },
          }}
        >
          {block.items.map((card, index) => (
            <WhyWebCard key={index} card={card} />
          ))}
        </Box>
      </Box>
    </LandingSection>
  );
}

/** `hero-visual_item`: a picture card at the wide steps, an icon row below them. */
const VISUAL_TILE = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  p: "16px",
  borderRadius: "8px",
  backgroundColor: "common.white",
  [TINY]: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "12px",
    p: "12px",
  },
} satisfies SxProps<Theme>;

function VisualTile({ card, span }: { card: Card; span: number }) {
  return (
    <Box
      sx={[
        VISUAL_TILE,
        { gridColumn: `span ${span}`, [TINY]: { gridColumn: "auto" } },
      ]}
    >
      {/* `hero-visual-mob`: the glyph the narrow step shows in the picture's place. */}
      {card.icon ? (
        <LandingImage
          src={card.icon}
          sx={{
            display: "none",
            width: "32px",
            height: "32px",
            flexShrink: 0,
            [TINY]: { display: "block" },
          }}
        />
      ) : null}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Typography
          component="h6"
          sx={{
            color: marketingPalette.ink,
            fontSize: 19,
            fontWeight: 400,
            lineHeight: "28.5px",
            [TINY]: { fontSize: 16, lineHeight: "20px" },
          }}
        >
          <Lines text={card.title} />
        </Typography>
        {card.body ? (
          <Typography
            sx={{
              color: marketingPalette.ink,
              fontSize: 14,
              lineHeight: "19.04px",
              opacity: 0.85,
            }}
          >
            {card.body}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          [TINY]: { display: "none" },
        }}
      >
        {card.image ? (
          <LandingImage src={card.image} sx={{ width: 1, height: "auto" }} />
        ) : null}
      </Box>
    </Box>
  );
}

const VISUAL_GRID = {
  display: "grid",
  gap: "12px",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  [NOT_WIDE]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  [TINY]: { gridTemplateColumns: "1fr", gap: "8px" },
} satisfies SxProps<Theme>;

function VisualRow({
  items,
  fill,
}: {
  items: ReadonlyArray<Card>;
  fill?: true;
}) {
  // The closing row runs short, and the published page widens its last card
  // over the columns the row would otherwise leave empty.
  const span = fill === true ? Math.max(1, 5 - items.length) : 1;
  return (
    <Box sx={VISUAL_GRID}>
      {items.map((card, index) => (
        <VisualTile
          key={index}
          card={card}
          span={index === items.length - 1 ? span : 1}
        />
      ))}
    </Box>
  );
}

/** `hero-visual_box`: the mint panel the alternative pages close their tour on. */
function KeyFeaturesPanel({ block }: { block: Cards }) {
  const rest = block.items.slice(4);
  return (
    <LandingSection padding="both">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          p: "32px",
          borderRadius: "32px",
          backgroundColor: marketingPalette.heroPanel,
          [NOT_WIDE]: { p: "16px" },
          [TINY]: { mt: "16px" },
        }}
      >
        <BandHeading block={block} gap="12.8px" rail="701.171px" />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            [TINY]: { gap: "8px" },
          }}
        >
          <VisualRow items={block.items.slice(0, 4)} />
          {rest.length ? <VisualRow items={rest} fill /> : null}
        </Box>
      </Box>
    </LandingSection>
  );
}

/**
 * The two picture bands the `visual` variant covers: the left-set support row,
 * and the mint panel of feature tiles. The heading's own alignment tells them
 * apart, as it does on the published pages.
 */
export default function CardsVisual({ block }: { block: Cards }) {
  if (block.headingAlign === "left") {
    return <WhyWebBand block={block} />;
  }
  return <KeyFeaturesPanel block={block} />;
}
